import React, { useState, useEffect, useCallback } from 'react';
import { Grid } from '@mui/material';
import { Group } from '@visx/group';
import { useTooltip } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { Text } from '@visx/text';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  getMonths2,
  getMonths,
  printFormat
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
} from '../../../../Assets/js/constant';
import appdata from '../../../../Services/appdata';

const TrendChart = ({
  detailData,
  summaryData,
  period,
  selectedYears,
  selectedTopItems,
  selectedMiddleItems,
  defaultStartMonth,
  defaultMonth,
  handleFilter,
}) => {
  const {
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
  } = useTooltip();

  const xSelector = (d) => (d && d !== undefined ? parseInt(d.month) : 0);
  const ySelector = (d) => (d && d !== undefined ? parseFloat(d.value) : 0);

  const [state, setState] = useState({
    resize: false,
    hoverBar: null,
    selectedBars: [],
    data: [],
    incomes: [],
    colors: [],
    selectedTopYears: [],
    lineClicked: false,
    lineYear: 0,
  });

  const updateState = useCallback((updates) => {
    setState((prevState) => {
      const nextUpdates =
        typeof updates === 'function' ? updates(prevState) : updates;

      if (!nextUpdates) return prevState;

      const hasChanges = Object.keys(nextUpdates).some(
        (key) => prevState[key] !== nextUpdates[key],
      );

      return hasChanges ? { ...prevState, ...nextUpdates } : prevState;
    });
  }, []);

  const onResize = useCallback(() => {
    updateState((prevState) => ({ resize: !prevState.resize }));
  }, [updateState]);

  const prepareData = useCallback(() => {
    let selectedTopYears = [];
    let dictData = {};
    let data = [];

    let currentSelectedYears = selectedYears.slice();
    const allIndex = currentSelectedYears.indexOf('All');
    if (allIndex > -1) currentSelectedYears.splice(allIndex, 1);

    let colors = currentSelectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    if (selectedTopItems.length === 0 || selectedMiddleItems.length === 0) {
      currentSelectedYears.forEach((item) => {
        selectedTopYears.push(item);
      });
    } else {
      selectedTopItems.forEach((item) => {
        if (selectedTopYears.indexOf(parseInt(item.year)) === -1)
          selectedTopYears.push(parseInt(item.year));
      });
    }

    if (period !== 'month') currentSelectedYears = currentSelectedYears.sort((a, b) => a - b);

    let fiscalMonths = getMonths(defaultStartMonth);
    let fiscalMonths2 = getMonths2(defaultMonth, defaultStartMonth);

    selectedTopYears.forEach((year) => {
      dictData[year] = {};
      dictData[year]['months'] = [];
      dictData[year]['totals'] = [];
      dictData[year]['forcasts'] = [];
      dictData[year]['year'] = 0;

      fiscalMonths.forEach((month) => {
        dictData[year]['months'][month] = {};
        dictData[year]['months'][month]['month'] = month;
        dictData[year]['months'][month]['value'] = 0;
        dictData[year]['months'][month]['totalvalue'] = 0;
        dictData[year]['totals'][month] = 0;
        dictData[year]['forcasts'][month] = 0;
      });
    });

    let accounts = [];
    if (detailData !== undefined) {
      const accountSelector = (d) => d.AccountType;
      accounts = detailData.map(accountSelector);
    }

    if (detailData)
      detailData.forEach((item) => {
        let month = getMonth(item.Date);

        if (
          selectedTopYears.indexOf(parseInt(item.FY)) > -1 &&
          item.AccountType !== 'Bonuses' &&
          item.AccountType !== 'Other Income' &&
          item.AccountType !== 'Extra Ordinary'
        ) {
          if (dictData[parseInt(item.FY)]['months'][month] !== undefined) {
            if (fiscalMonths2.indexOf(month) > -1) {              
              const actualExpenses = parseFloat(item.ActualExpenses) || 0;
              const forecastExpenses = parseFloat(item.ForecastExpenses) || 0;
              const includeInSelectedExpense =
                selectedMiddleItems.length === 0 ||
                selectedMiddleItems.indexOf(item.AccountType) > -1;

              if (includeInSelectedExpense) {
                dictData[parseInt(item.FY)]['months'][month]['value'] += actualExpenses;
                dictData[parseInt(item.FY)]['totals'][month] += actualExpenses;
                dictData[parseInt(item.FY)]['forcasts'][month] += forecastExpenses;
              }

              dictData[parseInt(item.FY)]['months'][month]['totalvalue'] +=
                actualExpenses;
              dictData[parseInt(item.FY)]['year'] = parseInt(item.FY);
            }
          }
        }
      });

    selectedTopYears.forEach((year) => {
      // arrange according to the financial year month
      let _newItem = [];
      fiscalMonths.forEach((month) => {
        _newItem.push(dictData[year]['months'][month]);
      });
      dictData[year]['months'] = _newItem;
      data.push(dictData[year]);
    });

    updateState({
      data: data,
      colors: colors,
    });
  }, [detailData, selectedYears, period, selectedTopItems, selectedMiddleItems, defaultStartMonth, defaultMonth, updateState]);

  const deSelectAll = useCallback(() => {
    const { hoverBar, selectedBars } = state;

    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    updateState({
      hoverBar: null,
      selectedBars: [],
    });

    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  }, [state, updateState, handleFilter]);

  // Component mount effect
  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  // Component update effect for detailData, selectedTopItems, selectedMiddleItems
  useEffect(() => {
    prepareData();
  }, [prepareData]);

  // Component update effect for summaryData
  useEffect(() => {
    if (summaryData && summaryData['income']) {
      let incomes = [];
      summaryData['income'].forEach((item) => {
        let income_data = {
          month: Math.floor((item.DimDate % 10000) / 100) - 1,
          value: item.Income,
        };
        incomes.push(income_data);
      });
      updateState({ incomes });
    }
  }, [summaryData, updateState]);

  const { data, incomes, lineClicked, lineYear } = state;

  const width = window.innerWidth - 16;
  const height = window.innerHeight / 6.5;
  const margin = {
    top: 10,
    right: 0,
    bottom: 50,
    left: 0,
  };

  const { xMax, yMax } = getParams(window, width, height, margin);
  const expenseChartXTransform = parseInt(
    appdata.getLocalItem('expenseByChartxTransform'),
    10,
  );
  const expenseChartLeft = Number.isFinite(expenseChartXTransform)
    ? expenseChartXTransform
    : 0;

  return (
    <div id="expense-trend-chart" style={{ height: 168 }}>
      <div className="subtitle mt-5 mb-5">Trend for Selected Expenses</div>

      <Grid container>
        {data.map((item, key) => {
          let validMonths = [];

          item.months.forEach((d, k) => {
            if (d.value !== 0 || d.totalvalue !== 0) validMonths[k] = d;
          });

          let totalmax = 0;
          Object.keys(item.totals).map(
            (i) => (totalmax = Math.max(item.totals[i], totalmax)),
          );

          let maxY = 0;
          let trendMonths = validMonths.map((month) => ({
            ...month,
            value: parseFloat(month.value) || 0,
          }));

          if (incomes.length > 0) {
            const incomeByMonth = {};

            incomes.forEach((income) => {
              const incomeMonth = parseInt(income.month, 10);
              if (Number.isFinite(incomeMonth)) {
                incomeByMonth[incomeMonth] = parseFloat(income.value) || 0;
              }
            });

            trendMonths = trendMonths.map((month) => {
              const income = incomeByMonth[parseInt(month.month, 10)];
              const expense = parseFloat(month.value) || 0;

              return {
                ...month,
                value: income ? Math.round((expense * 100) / income) : 0,
              };
            });

            trendMonths.forEach((month) => {
              maxY = Math.max(month.value, maxY);
            });
            maxY = maxY > 100 ? maxY : 100;
          }

          const safeXMax = Number.isFinite(xMax) ? xMax : 0;
          const safeYMax = Number.isFinite(yMax) ? yMax : 0;
          const chartCount = Math.max(data.length, 1);
          const xRangeMax = Math.max(safeXMax / chartCount - 45, 0);
          const yDomainMax = maxY || totalmax * 1.2 || 100;

          // scales
          let xScale = scaleBand()
            .domain(item.months.map(xSelector))
            .padding(0.2)
            .rangeRound([0, xRangeMax]);

          let yScale = scaleLinear({
            domain: [0, yDomainMax],
            range: [safeYMax + 20, 0],
            nice: true,
          });
          let labelScale = scaleLinear({
            domain: [0, maxY ? maxY : 100],
            range: [safeYMax + 20, 0],
            nice: true,
          });
          
          const lineMonths = trendMonths.filter((d) => {
            const monthX = xScale(xSelector(d));
            const monthY = yScale(ySelector(d));
            return Number.isFinite(monthX) && Number.isFinite(monthY);
          });

          let item_width = Math.round(12 / Math.max(data.length, 1));

          return (
            <Grid
              key={key}
              size={{ xs: item_width, sm: item_width, md: item_width }}
            >
              <svg width={width / data.length} height={height}>
                <rect
                  x={0}
                  y={0}
                  width={width / data.length}
                  height={height}
                  fill="transparent"
                  onClick={deSelectAll}
                />
                <Group
                  top={margin.top}
                  left={expenseChartLeft}
                >
                  <AxisLeft
                    numTicks={4}
                    scale={labelScale}
                    stroke="black"
                    tickStroke="black"
                    tickLabelProps={(value, index) => ({
                      fill: 'black',
                      fontSize: 11,
                      textAnchor: 'end',
                      dy: '0.33em',
                      dx: '-0.2em',
                    })}
                    tickComponent={({ formattedValue, ...tickProps }) => (
                      <text {...tickProps} fill="black">
                        {formattedValue}%
                      </text>
                    )}
                  />
                  
                  {incomes.length > 0 &&
                    trendMonths.map((month) => {
                      const monthX = xScale(month.month);
                      const monthY = yScale(month.value);

                      if (!Number.isFinite(monthX) || !Number.isFinite(monthY)) {
                        return null;
                      }

                      return (
                        <g key={month.month}>
                          <circle
                            cx={monthX + xScale.step() / 2 - 10}
                            cy={monthY}
                            stroke="#919698"
                            r={2}
                          />
                          <Text
                            x={monthX + xScale.step() / 2}
                            y={monthY}
                            verticalAnchor="end"
                            textAnchor="start"
                            fontSize="11px"
                            dx={-5}
                            dy={-5}
                          >
                            {printFormat.percentage(Math.round(month.value))}
                          </Text>
                        </g>
                      );
                    })}

                  <LinePath
                    data={lineMonths}
                    x={(d) => xScale(xSelector(d)) + xScale.step() / 2 - 10}
                    y={(d) => yScale(ySelector(d))}
                    strokeWidth={2}
                    stroke="#919698"
                    strokeLinecap="round"
                    fill="transparent"
                  />

                  {tooltipOpen &&
                    Number.isFinite(tooltipLeft) &&
                    Number.isFinite(tooltipTop) && (
                    <g>
                      <circle
                        cx={tooltipLeft - 10}
                        cy={tooltipTop}
                        r={lineYear === item.year ? (lineClicked ? 6 : 4) : 0}
                        fill="#919698"
                        stroke="#e3e3e3"
                        strokeWidth={lineClicked ? 3 : 0}
                        style={{ pointerEvents: 'none' }}
                      />
                    </g>
                  )}

                  <AxisBottom
                    top={safeYMax + 20}
                    scale={xScale}
                    hideTicks={true}
                    tickLabelProps={(value, index) => ({
                      fill: 'black',
                      fontSize: 11,
                      textAnchor: 'middle',
                      dy: '-0.3em',
                    })}
                    stroke="black"
                    tickStroke="black"
                    tickComponent={({ formattedValue, ...tickProps }) => (
                      <text {...tickProps}>{enMonths[formattedValue]}</text>
                    )}
                  />
                </Group>
              </svg>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

TrendChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  summaryData: PropTypes.object,
  period: PropTypes.string.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TrendChart;
